import { decodeHeicToBitmap, isHeic } from './receipt-file';

// Long edge (px) of the display rendition. Matches STORAGE_MAX_EDGE in the
// backend's image-heic.ts, so a file downscaled here passes server-side
// normalization untouched instead of picking up a second JPEG generation.
const MAX_EDGE = 2800;
const DISPLAY_QUALITY = 0.85;
// The original is the archival copy and the one the desktop viewer magnifies, so
// it is encoded near-lossless. A 4032x3024 frame lands around 3-4MB at this
// quality, well under the 20MB upload cap.
const ORIGINAL_QUALITY = 0.95;
const DISPLAY_TYPE = 'image/jpeg';

export interface DownscaleResult {
  /** What gets uploaded through the API and rendered in the grid, editor and PDF. */
  display: File;
  /**
   * The full-resolution copy, for the presigned direct-to-R2 upload. Usually the
   * untouched bytes the user picked; a HEIC is re-encoded to JPEG at its native
   * size instead, because no browser but Safari can render one.
   *
   * Null when there is no second copy worth storing — non-images, images the
   * browser couldn't decode, and a full-size encode that failed.
   */
  original: File | null;
}

// Browsers often report an empty MIME type for HEIC, so fall back to the
// extension check the receipt uploader already uses.
function isImage(file: File): boolean {
  return file.type.startsWith('image/') || isHeic(file);
}

function jpegName(name: string): string {
  return `${name.replace(/\.[^.]+$/, '')}.jpg`;
}

/** Draw the bitmap at `width`x`height` and encode it as JPEG. */
async function encode(
  bitmap: ImageBitmap,
  width: number,
  height: number,
  quality: number,
): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(bitmap, 0, 0, width, height);
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, DISPLAY_TYPE, quality));
}

/**
 * The full-size JPEG that stands in for a HEIC original.
 *
 * Null when the encode fails, which is the likeliest step to: it is the largest
 * allocation of the batch. The display rendition is already made and already
 * good, and a row with no original is a state every read path falls back from,
 * so a failure here costs sharpness on desktop and nothing else.
 */
async function encodeOriginal(
  bitmap: ImageBitmap,
  file: File,
  name: string,
): Promise<File | null> {
  const blob = await encode(bitmap, bitmap.width, bitmap.height, ORIGINAL_QUALITY);
  if (!blob) return null;
  return new File([blob], name, { type: DISPLAY_TYPE, lastModified: file.lastModified });
}

export interface DownscaleOptions {
  /**
   * Decode a HEIC the browser can't read itself, via libheif in a worker,
   * rather than handing the original straight to the server.
   *
   * Off by default, because it costs 1-3s per image — see the note on the
   * bulk-photo case in `downscaleForUpload`. Worth it for a receipt batch,
   * which is a handful of files and where the server decode it replaces lands
   * on a 1GB VM.
   */
  heicFallback?: boolean;
  /**
   * Produce the full-resolution `original` alongside the display rendition.
   *
   * On by default, for the job files library, where the original is the archival
   * copy the desktop viewer magnifies. Receipts have no such reader and discard
   * it — and for a HEIC that copy is a native-size JPEG encode, the largest
   * allocation in the batch, so making it and throwing it away is exactly the
   * work that puts a phone under memory pressure.
   */
  withOriginal?: boolean;
}

/**
 * Shrink an image in the browser before it is uploaded. A 4MB iPhone photo
 * comes back around 600KB, which is the difference between a 50-photo batch
 * moving ~200MB and ~35MB.
 *
 * The libheif fallback is off unless asked for: it costs 1-3s per image, worse
 * than just uploading the original across a large batch. iOS Safari decodes
 * HEIC natively via createImageBitmap, and iOS is the common bulk-photo case,
 * so that path is rare in practice. Callers uploading a few files at a time
 * should pass `heicFallback` — see DownscaleOptions.
 */
export async function downscaleForUpload(
  file: File,
  options: DownscaleOptions = {},
): Promise<DownscaleResult> {
  if (!isImage(file)) return { display: file, original: null };

  let bitmap: ImageBitmap;
  try {
    // from-image is not optional here. A phone photo carries its rotation as an
    // EXIF tag, the canvas below writes a JPEG with no EXIF at all, and the
    // backend's sharp .rotate() can only honour a tag that survived. Decode it
    // upright or it lands sideways. The bitmap then reports the oriented width
    // and height, so the scale maths needs no adjustment.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // HEIC outside Safari, or an encoding this browser can't decode.
    const decoded = options.heicFallback && isHeic(file)
      ? await decodeHeicToBitmap(file)
      : null;
    // Nothing decoded it. The server normalizes what it can, and there is no
    // second copy to preserve.
    if (!decoded) return { display: file, original: null };
    // libheif applies the container's rotation while decoding, so this bitmap
    // is already upright — the same contract the createImageBitmap path above
    // establishes with `imageOrientation: 'from-image'`.
    bitmap = decoded;
  }

  try {
    if (!bitmap.width || !bitmap.height) return { display: file, original: null };

    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const blob = await encode(
      bitmap,
      Math.round(bitmap.width * scale),
      Math.round(bitmap.height * scale),
      DISPLAY_QUALITY,
    );
    if (!blob) return { display: file, original: null };

    const display = new File([blob], jpegName(file.name), {
      type: DISPLAY_TYPE,
      lastModified: file.lastModified,
    });

    // A HEIC's own bytes are useless as the stored original: no browser outside
    // Safari renders one, so the desktop viewer can't magnify it and a Windows
    // desktop can't open the download. Re-encoding at native size keeps the
    // resolution, which is what an original is for; EXIF and byte-exactness are
    // the price. Everything else keeps the file the user picked.
    const withOriginal = options.withOriginal ?? true;
    const original = !withOriginal
      ? null
      : isHeic(file)
        ? await encodeOriginal(bitmap, file, display.name)
        : file;

    return { display, original };
  } catch (err) {
    console.error('Failed to downscale image, uploading the original:', err);
    return { display: file, original: null };
  } finally {
    // Mobile Safari holds decoded bitmaps until they are closed; a 50-file
    // batch accumulates them otherwise.
    bitmap.close();
  }
}
